/* eslint-disable react-hooks/exhaustive-deps */

import React, { useEffect, useState } from "react";
import styled, { css } from "styled-components";

import LayoutPage from "layouts/LayoutPage";
import PasswordProtectedContent from "components/PasswordProtectedContent";
import SectionHeading from "components/SectionHeading";
import { fetchReplicasFAQ } from "utils/functions/google-sheets";
import { generateQuoteLink } from "utils/functions/secure-link";
import { paypal } from "config";

const Wrapper = styled.div`
  width: 100%;
  max-width: 800px;
  margin-top: 50px;
  padding: 20px;

  ${(props) => props.theme.breakpoints.S} {
    padding: 2vw;
  }

  display: flex;
  flex-direction: column;
  align-items: stretch;
  gap: 30px;

  background: ${(props) => props.theme.colors.white}DD;
  backdrop-filter: blur(10px);
  border-radius: 8px;

  color: ${(props) => props.theme.colors.black};
`;

const Section = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const SectionTitle = styled.div`
  font-size: 15px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.1em;
  color: ${(props) => props.theme.colors.black}AA;
  margin: auto;
  margin-top: 20px;
  text-align: center;
`;

const ReplicaList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const ReplicaCategory = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 12px;
  background: ${(props) => props.theme.colors.black}08;
  border-radius: 4px;
`;

const CategoryName = styled.div`
  font-size: 12px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: ${(props) => props.theme.colors.black}CC;
  margin-bottom: 4px;
`;

const ReplicaItems = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const ReplicaItem = styled.div<{ active: boolean }>`
  display: flex;
  align-items: center;
  gap: 10px;
  cursor: pointer;
  user-select: none;
  border-radius: 3px;

  font-size: 13px;

  &:hover {
    background: ${(props) => props.theme.colors.black}04;
  }

  ${(props) =>
    props.active &&
    css`
      background: ${(props) => props.theme.colors.cyan}22;
      &:hover {
        background: ${(props) => props.theme.colors.cyan}33;
      }
    `}
`;

const Checkbox = styled.input`
  width: 16px;
  height: 16px;
  min-width: 16px;
  pointer-events: none;
`;

const ReplicaPrice = styled.div`
  font-weight: 600;
  color: ${(props) => props.theme.colors.black}CC;
  min-width: 40px;
`;

const ReplicaName = styled.div`
  flex: 1;
`;

const Input = styled.input`
  width: 46px;
  font-size: 13px;
  font-weight: 600;
  text-align: right;
  padding: 5px;
  border: 1px solid ${(props) => props.theme.colors.black}22;
  border-radius: 3px;
  background: ${(props) => props.theme.colors.black}08;
  color: ${(props) => props.theme.colors.black};

  &:focus {
    outline: none;
    border-color: ${(props) => props.theme.colors.black}44;
  }
`;

const Summary = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
  padding: 16px;
  background: ${(props) => props.theme.colors.white};
  border-radius: 4px;
`;

const SummaryRow = styled.div`
  display: flex;
  font-size: 12px;
  padding: 4px 0;
`;

const SummaryLabel = styled.div`
  flex: 1;
  font-weight: 600;
  color: ${(props) => props.theme.colors.black}88;
`;

const SummaryValue = styled.div`
  flex: 0;
  font-weight: 700;
  color: ${(props) => props.theme.colors.black}CC;
`;

const DiscountedPriceDisplay = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const StrikethroughPrice = styled.span`
  text-decoration: line-through;
  color: ${(props) => props.theme.colors.black}88;
  font-weight: 600;
`;

const DiscountedPrice = styled.span`
  font-weight: 700;
  color: ${(props) => props.theme.colors.black}CC;
`;

const TotalRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;

  & {
    font-size: 20px;
    font-weight: 700;
    color: #3b7bbf;
  }

  margin-top: 10px;
  padding-top: 10px;
  border-top: 1px solid ${(props) => props.theme.colors.black}11;
`;

const GenerateButtonRow = styled.div`
  width: 100%;
  margin-top: 15px;
`;

const GenerateButton = styled.button`
  width: 100%;
  padding: 12px;
  background: #3b7bbf;
  color: white;
  border: none;
  border-radius: 4px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: background 0.2s ease;
  text-transform: uppercase;
  letter-spacing: 0.05em;

  &:hover {
    background: #2a5a8f;
  }

  &:active {
    background: #1f425f;
  }

  &:disabled {
    background: ${(props) => props.theme.colors.black}44;
    cursor: not-allowed;
  }
`;

const SelectedReplicasList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
  margin: 14px 0;
  padding: 14px 0;
  border: 1px solid ${(props) => props.theme.colors.black}11;
  border-left: none;
  border-right: none;
`;

const SelectedReplicaItem = styled.div`
  display: flex;
  justify-content: space-between;
  font-size: 11px;
  color: ${(props) => props.theme.colors.black}99;
`;

interface ReplicaData {
  replica: string;
  version: string;
  price: number;
  link?: string;
  instock?: boolean;
  acceptingorders?: boolean;
  description?: string;
}

interface PriceState {
  total: number;
  replicas: number;
  shipping: number;
  fees: number;
}

const ReplicasPaymentGenerator = () => {
  const envPassword = String(
    process.env.REACT_APP_REPLICAS_GENERATOR_PASSWORD || "",
  )
    .toLowerCase()
    .trim();

  // Generator state
  const [replicasByCategory, setReplicasByCategory] = useState<
    Record<string, ReplicaData[]>
  >({});
  const [selectedReplicas, setSelectedReplicas] = useState<ReplicaData[]>([]);
  const [shipping, setShipping] = useState(paypal.defaultShipping);
  const [discountPercentage, setDiscountPercentage] = useState(0);
  const [price, setPrice] = useState<PriceState>({
    total: 0,
    replicas: 0,
    shipping: paypal.defaultShipping,
    fees: 0,
  });
  const [loading, setLoading] = useState(true);

  // Load replicas from Google Sheets
  useEffect(() => {
    fetchReplicasFAQ({
      spreadsheetId: "1zS54t-v3s_dzjzFapfQnu2l9Lj2fDVNdl1LxHvpZXAw",
      filterByEnv: false,
    })
      .then((parsed) => {
        const groups = (parsed && parsed.replicasByCategory) || {};
        setReplicasByCategory(groups);
        setLoading(false);
      })
      .catch((err) => {
        console.error("Failed to load replicas sheet:", err);
        setLoading(false);
      });
  }, []);

  // Calculate price whenever replicas or shipping change
  useEffect(() => {
    const replicasPrice = selectedReplicas.reduce((sum: number, replica) => {
      return sum + getReplicaPrice(replica, discountPercentage);
    }, 0);

    const subtotal = replicasPrice + shipping;

    const fees = Math.ceil(
      subtotal * (paypal.fees.percentageFee / 100) + paypal.fees.fixedFee,
    );
    const total = subtotal + fees;

    setPrice({
      total,
      replicas: replicasPrice,
      shipping,
      fees,
    });
  }, [replicasByCategory, shipping, selectedReplicas, discountPercentage]);

  const getReplicaName = (replica: ReplicaData): string => {
    return `${replica.replica} - ${replica.version || replica.replica}`;
  };

  const getReplicaPrice = (
    replica: ReplicaData,
    discountPercentage?: number,
  ): number => {
    return discountPercentage
      ? Math.ceil(replica.price * (1 - discountPercentage / 100))
      : replica.price;
  };

  const isReplicaSelected = (replica: ReplicaData): boolean => {
    const name = getReplicaName(replica);
    return selectedReplicas.some((r) => getReplicaName(r) === name);
  };

  const handleReplicaToggle = (item: ReplicaData) => {
    const replicaName = getReplicaName(item);

    setSelectedReplicas((prev) => {
      const updated = isReplicaSelected(item)
        ? prev.filter((r) => getReplicaName(r) !== replicaName)
        : [...prev, item];

      // Reorder based on original list order from replicasByCategory
      const selectedSet = new Set(updated.map((r) => getReplicaName(r)));
      const reordered: ReplicaData[] = [];

      Object.entries(replicasByCategory).forEach(([, items]) => {
        items.forEach((replicaItem) => {
          if (selectedSet.has(getReplicaName(replicaItem))) {
            reordered.push(replicaItem);
          }
        });
      });

      return reordered;
    });
  };

  const handleGeneratePaymentPage = async () => {
    if (selectedReplicas.length === 0) {
      alert("Please select at least one replica");
      return;
    }

    const quoteData = {
      datetime_created: new Date().toISOString(),
      total: price.total,
      items: [
        ...selectedReplicas.map((r) => {
          const discountSuffix =
            discountPercentage > 0 ? ` (${discountPercentage}% OFF)` : "";
          return {
            name: getReplicaName(r) + discountSuffix,
            price: getReplicaPrice(r, discountPercentage),
          };
        }),
        {
          name: "Shipping",
          price: shipping,
        },
        {
          name: "Fees",
          price: price.fees,
        },
      ],
    };

    try {
      const link = await generateQuoteLink(quoteData);
      window.open(link, "_blank");
    } catch (error) {
      console.error("Failed to generate payment page:", error);
      alert("Failed to generate payment page");
    }
  };

  return (
    <LayoutPage>
      <SectionHeading title="Prop Replicas" subtitle="Payment Generator" />

      <PasswordProtectedContent
        expectedPassword={envPassword}
        localStorageId="pass_replicas_payment_generator"
      >
        <Wrapper>
          {loading ? (
            <div
              style={{ textAlign: "center", color: "#000", padding: "20px" }}
            >
              Loading replicas...
            </div>
          ) : (
            <>
              {/* Replicas Selection */}
              <Section>
                <SectionTitle>Selected Replicas</SectionTitle>
                <ReplicaList>
                  {Object.entries(replicasByCategory).map(
                    ([category, items]) => (
                      <ReplicaCategory key={category}>
                        <CategoryName>{category}</CategoryName>
                        <ReplicaItems>
                          {items.map((item: ReplicaData, idx: number) => {
                            const isActive = isReplicaSelected(item);

                            return (
                              <ReplicaItem
                                key={idx}
                                onClick={() => handleReplicaToggle(item)}
                                active={isActive}
                              >
                                <Checkbox
                                  type="checkbox"
                                  checked={isActive}
                                  readOnly
                                />
                                <ReplicaPrice>
                                  ${item.price || "—"}
                                </ReplicaPrice>
                                <ReplicaName>
                                  {item.version || category}
                                </ReplicaName>
                              </ReplicaItem>
                            );
                          })}
                        </ReplicaItems>
                      </ReplicaCategory>
                    ),
                  )}
                </ReplicaList>
              </Section>

              {/* Summary */}
              <Section>
                <SectionTitle>Summary</SectionTitle>

                <Summary>
                  <SummaryRow>
                    <SummaryLabel>Shipping ({paypal.currency}):</SummaryLabel>
                    <Input
                      type="number"
                      value={shipping}
                      onChange={(e) =>
                        setShipping(Math.max(0, parseInt(e.target.value) || 0))
                      }
                      min="0"
                      step="5"
                    />
                  </SummaryRow>

                  <SummaryRow>
                    <SummaryLabel>Discount (%):</SummaryLabel>
                    <Input
                      type="number"
                      value={discountPercentage}
                      onChange={(e) =>
                        setDiscountPercentage(
                          Math.max(0, parseFloat(e.target.value) || 0),
                        )
                      }
                      min="0"
                      step="10"
                    />
                  </SummaryRow>

                  {selectedReplicas.length > 0 && (
                    <SelectedReplicasList>
                      {selectedReplicas.map((replica, idx) => (
                        <SelectedReplicaItem key={idx}>
                          <span>{getReplicaName(replica)}</span>
                          {discountPercentage > 0 ? (
                            <DiscountedPriceDisplay>
                              <StrikethroughPrice>
                                ${replica.price}
                              </StrikethroughPrice>
                              <DiscountedPrice>
                                $
                                {Math.ceil(
                                  replica.price *
                                    (1 - discountPercentage / 100),
                                )}
                              </DiscountedPrice>
                            </DiscountedPriceDisplay>
                          ) : (
                            <span>${replica.price}</span>
                          )}
                        </SelectedReplicaItem>
                      ))}
                    </SelectedReplicasList>
                  )}

                  <SummaryRow>
                    <SummaryLabel>Replicas Total:</SummaryLabel>
                    <SummaryValue>${price.replicas}</SummaryValue>
                  </SummaryRow>

                  <SummaryRow>
                    <SummaryLabel>
                      Fees ({paypal.fees.percentageFee}% + $
                      {paypal.fees.fixedFee}):
                    </SummaryLabel>
                    <SummaryValue>${price.fees}</SummaryValue>
                  </SummaryRow>

                  <TotalRow>
                    <span>Total:</span>
                    <span>${price.total}</span>
                  </TotalRow>

                  <GenerateButtonRow>
                    <GenerateButton
                      onClick={handleGeneratePaymentPage}
                      disabled={selectedReplicas.length === 0}
                    >
                      Generate Payment Page
                    </GenerateButton>
                  </GenerateButtonRow>
                </Summary>
              </Section>
            </>
          )}
        </Wrapper>
      </PasswordProtectedContent>
    </LayoutPage>
  );
};

export default ReplicasPaymentGenerator;
